import React, { useEffect, useState } from "react";
import styles from "./ClientAccountChat.module.css";
import InboxItem from "../../components/InboxChat/InboxChat";
import { useTranslation } from "react-i18next";
import api, { socketBaseUrl } from "../../utils/axios/api";
import Chat from "../../components/Chat/Chat";
import { io, Socket } from "socket.io-client";
import defaultAvatar from "../../assets/defaultAvatar.svg";

interface Mensagem {
  senderName: string;
  messagePreview: string;
  unreadCount: number;
  profileImage: string;
  data: Date;
  chatType: "PRODUCT" | "ORDER";
  chatId: string;
  otherUserId: string;
  id?: string;
}

interface ChatInfo {
  chatType: "PRODUCT" | "ORDER";
  chatId: string;
  senderName: string;
  otherUserId: string;
}

function ClientAccountChat() {
  const opcoesOrdenacao = ["Mais recentes", "Mais antigos", "A-Z", "Z-A"];
  const [ordenacaoSelecionada, setOrdenacaoSelecionada] = useState(
    opcoesOrdenacao[0]
  );
  const [conversas, setConversas] = useState<Mensagem[]>([]);
  const [conversasOrdenadas, setConversasOrdenadas] = useState<Mensagem[]>([]);
  const [chatAberto, setChatAberto] = useState<ChatInfo | null>(null);
  const { t } = useTranslation();

  const clientId = localStorage.getItem("userId");
  const [socket, setSocket] = useState<Socket | null>(null);

  // Conectar ao Socket.IO
  useEffect(() => {
    if (clientId) {
      const newSocket = io(socketBaseUrl); // Substitua pela URL do seu servidor
      setSocket(newSocket);

      // Emitir evento de login com o ID do cliente
      newSocket.emit("login", clientId);

      // Listener para mensagens recebidas
      newSocket.on("chat message", (message: any) => {
        // Verificar se a mensagem pertence ao cliente
        if (
          message.receiverUserId === clientId ||
          message.senderUserId === clientId
        ) {
          setConversas((prevConversas) => {
            // Identificar o chatId baseado no chatType
            const currentChatId =
              message.chatType === "PRODUCT"
                ? message.productId
                : message.orderId;
            const conversaIndex = prevConversas.findIndex(
              (c) => c.chatId === currentChatId
            );

            if (conversaIndex !== -1) {
              // Atualizar a conversa existente
              const updatedConversas = [...prevConversas];
              updatedConversas[conversaIndex].messagePreview = message.message;
              updatedConversas[conversaIndex].data = new Date(
                message.createdAt
              );

              // Incrementar unreadCount apenas se a mensagem for do remetente
              if (message.senderUserId !== clientId) {
                updatedConversas[conversaIndex].unreadCount += 1;
              }

              return updatedConversas;
            } else {
              // Adicionar nova conversa
              return [
                ...prevConversas,
                {
                  senderName:
                    message.senderUserId === clientId
                      ? "Você"
                      : message.senderName,
                  messagePreview: message.message,
                  unreadCount: message.senderUserId !== clientId ? 1 : 0,
                  profileImage:
                    message.senderUserId !== clientId
                      ? message.senderImage || defaultAvatar
                      : "",
                  data: new Date(message.createdAt),
                  chatType: message.chatType,
                  chatId: currentChatId,
                  otherUserId:
                    message.senderUserId === clientId
                      ? message.receiverUserId
                      : message.senderUserId,
                },
              ];
            }
          });
        }
      });

      // Listener para erros do Socket.IO
      newSocket.on("error", (error: any) => {
        console.error("Erro no Socket.IO:", error);
      });

      // Cleanup na desconexão
      return () => {
        newSocket.disconnect();
      };
    }
  }, [clientId]);

  // Buscar conversas do backend
  useEffect(() => {
    async function loadChats() {
      if (clientId) {
        try {
          const response = await api.get(`/chat/user/${clientId}`);
          const conversasComTipos: Mensagem[] = response.data.map(
            (conversa: any) => ({
              senderName: conversa.senderName,
              messagePreview: conversa.messagePreview,
              unreadCount: conversa.unreadCount,
              profileImage: conversa.profileImage,
              data: new Date(conversa.data),
              chatType: conversa.chatType,
              chatId: conversa.chatId,
              otherUserId: conversa.otherUserId,
              id: conversa.id,
            })
          );
          setConversas(conversasComTipos);
          ordenarConversas(ordenacaoSelecionada, conversasComTipos);
        } catch (error) {
          console.error("Erro ao carregar conversas:", error);
        }
      }
    }
    loadChats();
  }, [clientId]);

  // Ordenar conversas sempre que houver mudança na ordenação ou nas conversas
  useEffect(() => {
    ordenarConversas(ordenacaoSelecionada, conversas);
  }, [ordenacaoSelecionada, conversas]);

  const handleOrdenacaoChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setOrdenacaoSelecionada(event.target.value);
  };

  const ordenarConversas = (
    opcao: string,
    conversasToSort: Mensagem[] = conversas
  ) => {
    const conversasCopia = [...conversasToSort];
    switch (opcao) {
      case "Mais recentes":
        conversasCopia.sort((a, b) => b.data.getTime() - a.data.getTime());
        break;
      case "Mais antigos":
        conversasCopia.sort((a, b) => a.data.getTime() - b.data.getTime());
        break;
      case "A-Z":
        conversasCopia.sort((a, b) => a.senderName.localeCompare(b.senderName));
        break;
      case "Z-A":
        conversasCopia.sort((a, b) => b.senderName.localeCompare(a.senderName));
        break;
      default:
        break;
    }
    setConversasOrdenadas(conversasCopia);
  };

  const handleChatClick = async (conversa: Mensagem) => {
    setChatAberto({
      chatType: conversa.chatType,
      chatId: conversa.chatId,
      senderName: conversa.senderName,
      otherUserId: conversa.otherUserId,
    });

    // Chamar API para marcar mensagens como lidas
    if (clientId && conversa.chatId) {
      console.log("Marcando mensagens como lidas:", clientId, conversa.chatId);
      api
        .post("/chat/markAsRead", {
          userId: clientId,
          chatId: conversa.id,
        })
        .then(() => {
          // Atualizar o estado local para refletir que as mensagens foram lidas
          setConversas((prevConversas) =>
            prevConversas.map((c) =>
              c.chatId === conversa.chatId ? { ...c, unreadCount: 0 } : c
            )
          );
        })
        .catch((error) => {
          console.error("Erro ao marcar mensagens como lidas:", error);
        });
    }
  };

  useEffect(() => {
    if (chatAberto && socket) {
      socket.on("chat message", (message: any) => {
        if (
          message.receiverUserId === clientId ||
          message.senderUserId === clientId
        ) {
          const currentChatId =
            message.chatType === "PRODUCT"
              ? message.productId
              : message.orderId;

          setConversas((prevConversas) => {
            const conversaIndex = prevConversas.findIndex(
              (c) => c.chatId === currentChatId
            );

            if (conversaIndex !== -1) {
              const updatedConversas = [...prevConversas];
              updatedConversas[conversaIndex].messagePreview = message.message;
              updatedConversas[conversaIndex].data = new Date(
                message.createdAt
              );

              // Verificar se o chat está aberto para zerar o contador de não lidas
              if (chatAberto && chatAberto.chatId === currentChatId) {
                updatedConversas[conversaIndex].unreadCount = 0;
              } else if (message.senderUserId !== clientId) {
                // Incrementar contador de não lidas apenas se não for o remetente
                updatedConversas[conversaIndex].unreadCount += 1;
              }

              return updatedConversas;
            } else {
              return [
                ...prevConversas,
                {
                  senderName:
                    message.senderUserId === clientId
                      ? "Você"
                      : message.senderName,
                  messagePreview: message.message,
                  unreadCount:
                    chatAberto && chatAberto.chatId === currentChatId
                      ? 0
                      : message.senderUserId !== clientId
                      ? 1
                      : 0,
                  profileImage:
                    message.senderUserId !== clientId
                      ? message.senderImage || defaultAvatar
                      : "",
                  data: new Date(message.createdAt),
                  chatType: message.chatType,
                  chatId: currentChatId,
                  otherUserId:
                    message.senderUserId === clientId
                      ? message.receiverUserId
                      : message.senderUserId,
                },
              ];
            }
          });
        }
      });
    }
  }, [chatAberto, socket]);

  const handleCloseChat = () => {
    setChatAberto(null);
  };

  return (
    <div>
      <main className={styles.visualizarchat}>
        <section className={styles.content}>
          <div className={styles.firstLine}>
            <h1 className={styles.titulo}>{t("visualizarConversa.chat")}</h1>
            <div className="custom-select"></div>
            <select
              value={ordenacaoSelecionada}
              onChange={handleOrdenacaoChange}
            >
              {opcoesOrdenacao.map((opcao, index) => (
                <option key={index} value={opcao}>
                  {opcao}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.chat}>
            {conversasOrdenadas.map((conversa, index) => (
              <InboxItem
                key={index}
                senderName={conversa.senderName}
                messagePreview={conversa.messagePreview}
                unreadCount={conversa.unreadCount}
                profileImage={conversa.profileImage}
                date={`${conversa.data.getDate()}/${
                  conversa.data.getMonth() + 1
                }/${conversa.data.getFullYear()}`}
                onClick={() => handleChatClick(conversa)} // Passando onClick
              />
            ))}
          </div>
        </section>
      </main>

      {/* Renderizar o Chat diretamente na página */}
      {chatAberto && socket && (
        <Chat
          socket={socket}
          type={chatAberto.chatType}
          id={chatAberto.chatId}
          onClose={handleCloseChat}
          infos={{
            otherUserId: chatAberto.otherUserId, // Use o ID correto do outro usuário
            userName: chatAberto.senderName,
          }}
        />
      )}
    </div>
  );
}

export default ClientAccountChat;
